import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';
import StatusChip from 'src/components/StatusChip';
import CardActionButton from 'src/components/CardActionButton';
import SummaryStatCard from 'src/components/summary/SummaryStatCard';
import { Loader } from 'src/components';
import { formatDate, formatRelativeDays } from 'src/utils/formatTime';
import { DEVICE_STATUS, getDeviceStatusLabel } from 'src/utils/enums/deviceStatus';
import { HEALTH_STATUS, getHealthStatusLabel } from 'src/utils/enums/healthStatus';
import {
  READINESS_STATUS,
  getReadinessStatusColor,
  getReadinessStatusLabel,
} from 'src/utils/enums/readinessStatus';
import { READINESS_FACTOR_TYPE } from 'src/utils/enums/readinessFactorType';
import { WDAC_STATUS, getWdacStatusLabel } from 'src/utils/enums/wdacStatus';
import { useSnackbar } from 'notistack';
import { useDeploymentRings } from 'src/hooks/deploymentRing';
import DeviceSettingsModal from './DeviceSettingsModal';
import DeviceReadinessAppsTable from './DeviceReadinessAppsTable';
import DeviceReadinessUnmatchedTable from './DeviceReadinessUnmatchedTable';
import {
  useDeviceDetails,
  useInfiniteDeviceReadinessApps,
  useInfiniteDeviceReadinessUnmatched,
  useDeviceReadinessSummary,
  useUpdateDeviceSettings,
} from 'src/hooks/device';
import useInfiniteScroll from 'react-infinite-scroll-hook';

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: '#484848',
  borderRadius: theme.spacing(1),
  padding: '24px',
  width: '100%',
  boxShadow: '0px 1px 2px 0px #64748B1A',
}));

const ThreeColumnGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
}));
const READINESS_WARNING_MESSAGE =
  'This device should be reviewed because it is Revoked/Archived or not Healthy, which signals it may not be ready for enforcement.';

const DeviceGeneralSection = () => {
  const { deviceId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading, data: deviceData } = useDeviceDetails({ deviceId });
  const [settingsOpen, setSettingsOpen] = useState(false);

  const device = deviceData?.device;
  const { data: deploymentRingsData } = useDeploymentRings();
  const deploymentRing = deploymentRingsData?.rings?.find(
    ring => ring.id === device?.deploymentRingId,
  );
  const tags = deviceData?.tags || [];
  const healthStatus = deviceData?.healthStatus;

  const wdacStatusLabel = getWdacStatusLabel(device?.wdacStatus);
  const isAuditMode = device?.wdacStatus === WDAC_STATUS.Audit;

  const readinessSummaryQuery = useDeviceReadinessSummary({
    deviceId,
    enabled: isAuditMode,
  });
  const readinessAppsQuery = useInfiniteDeviceReadinessApps({
    deviceId,
    enabled: isAuditMode,
    limit: 10,
  });
  const readinessUnmatchedQuery = useInfiniteDeviceReadinessUnmatched({
    deviceId,
    enabled: isAuditMode,
    limit: 10,
  });

  const readinessAppsItems = useMemo(
    () => readinessAppsQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [readinessAppsQuery.data],
  );
  const readinessUnmatchedItems = useMemo(
    () => readinessUnmatchedQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [readinessUnmatchedQuery.data],
  );

  const loadMoreReadinessApps = useCallback(() => {
    if (readinessAppsQuery.hasNextPage) {
      readinessAppsQuery.fetchNextPage();
    }
  }, [readinessAppsQuery]);

  const [readinessAppsSentryRef] = useInfiniteScroll({
    loading: readinessAppsQuery.isFetchingNextPage,
    hasNextPage: readinessAppsQuery.hasNextPage,
    disabled: !isAuditMode,
    onLoadMore: loadMoreReadinessApps,
    rootMargin: '0px 0px 400px 0px',
  });
  const loadMoreReadinessUnmatched = useCallback(() => {
    if (readinessUnmatchedQuery.hasNextPage) {
      readinessUnmatchedQuery.fetchNextPage();
    }
  }, [readinessUnmatchedQuery]);

  const [readinessUnmatchedSentryRef] = useInfiniteScroll({
    loading: readinessUnmatchedQuery.isFetchingNextPage,
    hasNextPage: readinessUnmatchedQuery.hasNextPage,
    disabled: !isAuditMode,
    onLoadMore: loadMoreReadinessUnmatched,
    rootMargin: '0px 0px 400px 0px',
  });

  const updateSettingsMutation = useUpdateDeviceSettings();

  const statusLabel = getDeviceStatusLabel(device?.status || DEVICE_STATUS.Unknown);

  const readinessSummary = readinessSummaryQuery.data;
  const readinessStatusKey = readinessSummary?.lastReadinessStatus;
  const readinessLabel = getReadinessStatusLabel(readinessStatusKey);
  const readinessColor = getReadinessStatusColor(readinessStatusKey);
  const readinessNote = readinessSummary?.lastReadinessNote ?? 'Readiness note is unavailable.';
  const lastReadinessUpdate = readinessSummary?.lastReadinessStatusUpdateDate;
  const matchedFactor = readinessSummary?.factors?.find(
    factor => factor.factorType === READINESS_FACTOR_TYPE.Win32Matched,
  );
  const unmatchedFactor = readinessSummary?.factors?.find(
    factor => factor.factorType === READINESS_FACTOR_TYPE.Win32Unmatched,
  );
  const healthStatusLabel = getHealthStatusLabel(healthStatus);
  const showReadinessWarning =
    readinessStatusKey &&
    readinessStatusKey !== READINESS_STATUS.NeedsWork &&
    (device?.status === DEVICE_STATUS.Archived ||
      device?.status === DEVICE_STATUS.Revoked ||
      healthStatus !== HEALTH_STATUS.Healthy);

  const onSaveSettings = useCallback(
    async ({ status, notes, tags: nextTags, deploymentRingId }) => {
      const tagIds = (nextTags || []).filter(tag => tag.id).map(tag => tag.id);
      const tagNames = (nextTags || [])
        .filter(tag => !tag.id)
        .map(tag => (tag.name || '').trim())
        .filter(Boolean);

      try {
        await updateSettingsMutation.mutateAsync({
          deviceId,
          status,
          notes,
          tagIds,
          tagNames,
          deploymentRingId,
        });
        enqueueSnackbar('Device settings updated.', {
          variant: 'success',
          autoHideDuration: 3000,
        });
        setSettingsOpen(false);
      } catch (error) {
        enqueueSnackbar('Something went wrong while updating device settings.', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    },
    [deviceId, enqueueSnackbar, updateSettingsMutation],
  );

  if (isLoading) {
    return (
      <Box mt="80px">
        <Loader />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <SummaryStatCard
        items={[
          {
            key: 'wdacStatus',
            label: 'WDAC Status',
            value: wdacStatusLabel,
            subtext: isAuditMode ? `Readiness: ${readinessLabel}` : null,
          },
          { key: 'deviceStatus', label: 'Device Status', value: statusLabel },
          {
            key: 'healthStatus',
            label: 'Health Status',
            value: healthStatusLabel,
            subtext: (
              <Stack spacing={0.25}>
                <Typography variant="caption" color="#CFCFCF">
                  Last heartbeat:{' '}
                  {device?.lastWdacHeartbeatDate ? formatDate(device.lastWdacHeartbeatDate) : '--'}
                </Typography>
                <Typography variant="caption" color="#CFCFCF">
                  Last contact:{' '}
                  {device?.lastContactDate ? formatDate(device.lastContactDate) : '--'}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'logCollectorVersion',
            label: 'Log Collector Version',
            value: device?.logCollectorVersion || 'Unknown',
          },
        ]}
      />

      <Card>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Settings for {device?.name || 'Device'}</Typography>
            <CardActionButton
              onClick={() => setSettingsOpen(true)}
              startIcon={<Iconify icon="mdi:pencil-outline" width={15} height={15} />}>
              Edit
            </CardActionButton>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 2,
            }}>
            <Box>
              <Typography variant="caption" color="#CFCFCF">
                Unique External Id
              </Typography>
              <Typography variant="body2">{device?.uniqueExternalId || '--'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="#CFCFCF">
                OS Version | OS SKU | OS Type
              </Typography>
              <Typography variant="body2">
                {device?.osVersion || '--'} | {device?.osSku || '--'} | {device?.osType || '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="#CFCFCF">
                Created
              </Typography>
              <Typography variant="body2">
                {device?.createdAt ? formatDate(device.createdAt) : '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="#CFCFCF">
                Updated
              </Typography>
              <Typography variant="body2">
                {device?.updatedAt ? formatDate(device.updatedAt) : '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="#CFCFCF">
                Deployment Ring
              </Typography>
              <Typography variant="body2">{deploymentRing?.name || '--'}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" color="#CFCFCF">
                Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {device?.notes || '--'}
              </Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" color="#CFCFCF">
                Device Tags
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                {tags.length ? (
                  tags.map(tag => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      sx={{
                        border: '0.5px solid #fff',
                        color: '#fff',
                        backgroundColor: '#3f3f3f',
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="body2">No tags</Typography>
                )}
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Card>

      {isAuditMode && (
        <Card>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Device Readiness</Typography>
              <Tooltip
                title={
                  'Readiness is based on device age, clean check-in streak, and last check-in recency. Devices must be in Audit mode. Statuses older than 7 days default to Needs Work.'
                }>
                <IconButton size="small">
                  <Iconify
                    icon="mdi:information-outline"
                    color={'#CFCFCF'}
                    width={20}
                    height={20}
                  />
                </IconButton>
              </Tooltip>
            </Stack>

            <ThreeColumnGrid>
              <Box>
                <Typography variant="caption" color="#CFCFCF">
                  Readiness Status
                </Typography>
                <Box mt={0.5} display="flex" alignItems="center" gap={1}>
                  <StatusChip
                    label={readinessLabel}
                    color={readinessColor}
                    backgroundColor={readinessColor}
                    textColor="#fff"
                  />
                  {showReadinessWarning ? (
                    <Tooltip title={READINESS_WARNING_MESSAGE}>
                      <Box display="inline-flex" alignItems="center">
                        <Iconify
                          icon="material-symbols:warning-outline-rounded"
                          color="#F4B740"
                          width={18}
                          height={18}
                        />
                      </Box>
                    </Tooltip>
                  ) : null}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="#CFCFCF">
                  Clean Check-In Streak
                </Typography>
                <Typography variant="body2">
                  {readinessSummary?.cleanCheckInCount ?? 0} clean check-ins
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="#CFCFCF">
                  Last Check-In
                </Typography>
                <Typography variant="body2">{formatRelativeDays(lastReadinessUpdate)}</Typography>
              </Box>
            </ThreeColumnGrid>

            <Typography variant="body2">{readinessNote}</Typography>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Matched Apps</Typography>
                <Typography variant="body2" sx={{ color: '#CFCFCF' }}>
                  ({matchedFactor?.countLast30Days ?? 0} Win32 Matched apps, last seen{' '}
                  {formatRelativeDays(matchedFactor?.lastEventDate)})
                </Typography>
              </Stack>
              <DeviceReadinessAppsTable
                items={readinessAppsItems}
                isLoading={readinessAppsQuery.isLoading}
                isFetchingNextPage={readinessAppsQuery.isFetchingNextPage}
                sentryRef={readinessAppsSentryRef}
              />
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Unmatched Files</Typography>
                <Typography variant="body2" sx={{ color: '#CFCFCF' }}>
                  ({unmatchedFactor?.countLast30Days ?? 0} Win32 Unmatched files, last seen{' '}
                  {formatRelativeDays(unmatchedFactor?.lastEventDate)})
                </Typography>
              </Stack>
              <DeviceReadinessUnmatchedTable
                items={readinessUnmatchedItems}
                isLoading={readinessUnmatchedQuery.isLoading}
                isFetchingNextPage={readinessUnmatchedQuery.isFetchingNextPage}
                sentryRef={readinessUnmatchedSentryRef}
              />
            </Box>
          </Stack>
        </Card>
      )}

      <DeviceSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        device={device}
        tags={tags}
        onSave={onSaveSettings}
        isSaving={updateSettingsMutation.isLoading}
      />
    </Stack>
  );
};

export default DeviceGeneralSection;
